import { useState, useEffect, useMemo } from 'react';
import { MapPin, AlertCircle, Loader2, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LocationSelectionModal from './LocationSelectionModal';
import DateTimeSelector from './DateTimeSelector';
import { useDeliveryCoverage, useOrderDelays, useAsapAvailability } from '@/hooks/useOrderableMenu';
import { useUserProfile, SavedAddress } from '@/hooks/useUserProfile';
import { useAsapQuoteRefresh } from '@/utils/asapQuote';

// Define SavedLocation type locally to match LocationSelectionModal
interface SavedLocation {
  id: string;
  name: string;
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

// Common params returned from the modal
export interface OrderConfigParams {
  serviceType: 'delivery' | 'takeout';
  timing: 'asap' | 'scheduled';
  scheduledTime?: string;
  scheduledStart?: number;
  scheduledEnd?: number;
  location: string;
  coordinates?: { lat: number; lng: number };
  radius?: number;
  addressId?: number;
}

// Type definitions for hours structure (matches DateTimeSelector)
interface ServiceHours {
  open?: Record<string, string>;
  close?: Record<string, string>;
  open2?: Record<string, string>;
  close2?: Record<string, string>;
}

interface HoursJson {
  delivery?: ServiceHours;
  takeout?: ServiceHours;
  business?: ServiceHours;
}

interface OrderInitiationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: (params: OrderConfigParams) => void;
  defaultLocation?: string;
  restaurantId?: string;
  mode?: 'discovery' | 'order';
  defaultScheduledTime?: Date;  // Pre-set scheduled time (e.g., next opening time for closed restaurants)
  isRestaurantOpen?: boolean;   // When false, ASAP is disabled in DateTimeSelector
  initialServiceType?: 'delivery' | 'takeout';  // Pre-select service type when editing
  initialTiming?: 'asap' | 'scheduled';         // Pre-select timing when editing
  initialScheduledTime?: string;                 // Pre-set ISO scheduled time when editing
  initialAddressId?: string;                     // Pre-select address when editing
  hoursJson?: HoursJson | null;  // Restaurant operating hours for filtering time slots
  timezone?: string | null;       // Restaurant timezone
  /** Session location passed down from the parent page (single source of truth). */
  sessionLocationName?: string;
  sessionCoordinates?: { lat: number; lng: number } | null;
  /** Called when user picks a new location so the parent can update its session state. */
  onSessionLocationChange?: (name: string, lat?: number, lng?: number) => void;
}

export default function OrderInitiationModal({ 
  isOpen, 
  onClose, 
  onProceed, 
  defaultLocation = '',
  restaurantId,
  mode = 'discovery',
  defaultScheduledTime,
  isRestaurantOpen,
  initialServiceType,
  initialTiming,
  initialScheduledTime,
  initialAddressId,
  hoursJson,
  timezone,
  sessionLocationName: sessionLocName,
  sessionCoordinates: sessionCoords,
  onSessionLocationChange,
}: OrderInitiationModalProps) {
  // Determine initial timing: use initialTiming if provided, otherwise check if restaurant is closed
  const getInitialTiming = () => {
    if (initialTiming) return initialTiming;
    if (!isRestaurantOpen || defaultScheduledTime) return 'scheduled';
    return 'asap';
  };
  
  // Determine initial scheduled time: use initialScheduledTime if provided, then defaultScheduledTime
  const getInitialScheduledTime = () => {
    if (initialScheduledTime) return initialScheduledTime;
    if (defaultScheduledTime) return defaultScheduledTime.toISOString();
    return '';
  };
  
  const [serviceType, setServiceType] = useState<'delivery' | 'takeout'>(initialServiceType || 'delivery');
  const [timing, setTiming] = useState<'asap' | 'scheduled'>(getInitialTiming());
  const [scheduledTime, setScheduledTime] = useState(getInitialScheduledTime());
  const [selectedLocation, setSelectedLocation] = useState<'current' | SavedLocation | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [radius, setRadius] = useState<number>(2);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Get user's saved addresses
  const { addresses, primaryAddress, isLoading: addressesLoading } = useUserProfile();

  // Session location write-back is handled via the onSessionLocationChange prop (parent owns state)

  // Find the currently selected address
  const selectedAddress = selectedAddressId 
    ? addresses.find(a => a.id.toString() === selectedAddressId) 
    : null;

  // Get coordinates for coverage check - prefer user's saved address in order mode
  // Use typeof check to allow 0 as a valid coordinate value
  const hasAddressCoordinates = mode === 'order' && selectedAddress && 
    typeof selectedAddress.latitude === 'number' && 
    typeof selectedAddress.longitude === 'number';

  // Session-location fallback only applies when the user has NO saved addresses (guest path).
  // Computed early so it can be used in both coordinates and hasValidLocation below.
  const noSavedAddresses = mode === 'order' && serviceType === 'delivery' && !addressesLoading && addresses.length === 0;
  
  const coordinates = hasAddressCoordinates
    ? { lat: selectedAddress.latitude!, lng: selectedAddress.longitude! }
    : selectedLocation && selectedLocation !== 'current' && selectedLocation.coordinates
      ? selectedLocation.coordinates 
      // Session coords fallback: only when user has no saved addresses (guest path)
      : (noSavedAddresses)
        ? sessionCoords
        : null;

  // Check delivery coverage in order mode — only when modal is open
  const { data: coverageData, isLoading: coverageLoading, error: coverageError } = useDeliveryCoverage(
    mode === 'order' && isOpen && serviceType === 'delivery' ? restaurantId : undefined,
    coordinates?.lat ?? null,
    coordinates?.lng ?? null
  );

  // Check ASAP availability — only when modal is open
  const { data: asapAvailability, isLoading: asapLoading } = useAsapAvailability(
    mode === 'order' && isOpen ? restaurantId : undefined,
    serviceType,
    mode === 'order' && isOpen
  );

  // Get estimated delays — only when modal is open
  const { data: delaysData } = useOrderDelays(
    mode === 'order' && isOpen ? restaurantId : undefined
  );

  // Convert delaysData to menu delays format for ASAP quote calculation (memoized to prevent infinite loops)
  // For delivery, only use delays if we have coordinates (address selected) - otherwise route time is inaccurate
  const menuDelays = useMemo(() => {
    // Prefer delays from asapAvailability (from /menu/asap endpoint) as they're more accurate
    if (asapAvailability?.delays) {
      return asapAvailability.delays;
    }
    if (!delaysData) return undefined;
    return {
      window: 900, // Default 15-minute window
      delay: delaysData.takeoutSeconds, // Time until pickup
      route: delaysData.deliverySeconds, // Note: without address, this doesn't include drive time
    };
  }, [asapAvailability?.delays, delaysData?.takeoutSeconds, delaysData?.deliverySeconds]);

  // For delivery, only show time estimate when we have a selected address with coordinates
  // For takeout, we can show immediately since no drive time is needed
  const hasAddressForDeliveryQuote = serviceType === 'takeout' || (coordinates !== null);

  // Auto-refreshing ASAP quote (updates every minute) - use restaurant timezone for display
  // Only calculate if we have the right data for the service type
  const asapQuote = useAsapQuoteRefresh(
    hasAddressForDeliveryQuote ? menuDelays : undefined, 
    serviceType, 
    timezone
  );

  // Determine if ASAP is actually available (based on menu/asap endpoint, not just liveStatus)
  // When loading, keep the previous state or use liveStatus as fallback
  const isAsapActuallyAvailable = useMemo(() => {
    // In order mode, prefer the menu/asap check result
    if (mode === 'order' && !asapLoading && asapAvailability !== undefined) {
      return asapAvailability.available;
    }
    // While loading or in discovery mode, use liveStatus as fallback
    return isRestaurantOpen ?? true;
  }, [mode, asapLoading, asapAvailability?.available, isRestaurantOpen]);

  // Set default location when modal opens or defaultLocation changes
  useEffect(() => {
    if (defaultLocation && !selectedLocation) {
      setSelectedLocation({
        id: 'default',
        name: defaultLocation,
        address: defaultLocation
      });
    }
  }, [defaultLocation]);

  // Pre-select primary address when addresses load (order mode)
  useEffect(() => {
    if (mode === 'order' && addresses.length > 0 && !selectedAddressId) {
      // Select primary address, or first address if no primary
      const defaultAddr = primaryAddress || addresses[0];
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id.toString());
      }
    }
  }, [mode, addresses, primaryAddress, selectedAddressId]);

  // Reset state when modal opens - respect initial values for editing
  useEffect(() => {
    if (isOpen) {
      // Reset service type - use initial if provided
      setServiceType(initialServiceType || 'delivery');
      
      // Set address - use initial if provided
      if (initialAddressId) {
        setSelectedAddressId(initialAddressId);
      }
      
      // Determine timing
      if (initialTiming) {
        setTiming(initialTiming);
        setScheduledTime(initialScheduledTime || '');
      } else if (defaultScheduledTime) {
        setTiming('scheduled');
        setScheduledTime(defaultScheduledTime.toISOString());
      } else if (!isRestaurantOpen) {
        // Restaurant closed - force scheduled timing, clear scheduled time so user must select
        setTiming('scheduled');
        setScheduledTime('');
      } else {
        // Restaurant open, no default - reset to ASAP
        setTiming('asap');
        setScheduledTime('');
      }
    }
  }, [isOpen, defaultScheduledTime, isRestaurantOpen, initialServiceType, initialTiming, initialScheduledTime, initialAddressId]);

  // Auto-switch to scheduled timing if ASAP availability check determines it's not available
  // This runs after the initial open effect and after asapAvailability loads
  useEffect(() => {
    if (isOpen && mode === 'order' && !asapLoading && asapAvailability) {
      if (!asapAvailability.available && timing === 'asap') {
        console.log('[OrderInitiationModal] ASAP not available via menu/asap endpoint, switching to scheduled');
        setTiming('scheduled');
        setScheduledTime('');
      }
    }
  }, [isOpen, mode, asapLoading, asapAvailability?.available, timing]);

  // Helper to format address for display
  const formatAddressDisplay = (addr: SavedAddress): string => {
    const parts = [addr.streetAddress];
    if (addr.apartment) parts.push(`#${addr.apartment}`);
    parts.push(`${addr.city}, ${addr.state || ''} ${addr.zipCode}`.trim());
    return parts.join(', ');
  };

  // Helper to get short address label
  const getAddressLabel = (addr: SavedAddress): string => {
    return addr.label || `${addr.streetAddress}, ${addr.city}`;
  };

  const handleTimeSelect = (date: Date | null, isAsap: boolean) => {
    if (isAsap) {
      setTiming('asap');
      setScheduledTime('');
    } else if (date) {
      setTiming('scheduled');
      setScheduledTime(date.toISOString());
    }
  };

  const handleProceed = () => {
    let locationString = '';
    let coords: { lat: number; lng: number } | undefined;
    
    // In order mode, determine location with clear priority:
    //   1. Saved address (selected from profile)
    //   2. Freshly picked location from this modal session (selectedLocation)
    //   3. Session location props from parent (fallback for unmodified state)
    if (mode === 'order' && selectedAddress) {
      locationString = formatAddressDisplay(selectedAddress);
      if (selectedAddress.latitude && selectedAddress.longitude) {
        coords = { lat: selectedAddress.latitude, lng: selectedAddress.longitude };
      }
    } else if (mode === 'order' && selectedLocation && selectedLocation !== 'current' && selectedLocation.coordinates) {
      // User actively picked a location with coordinates — this is authoritative
      locationString = selectedLocation.address || selectedLocation.name || '';
      coords = selectedLocation.coordinates;
    } else if (mode === 'order' && sessionLocName) {
      // No address, no coordinate-bearing picker selection — use pre-existing session location
      // This covers: (a) initial load with session prefill, (b) selectedLocation seeded from defaultLocation string only
      locationString = sessionLocName;
      if (sessionCoords) coords = sessionCoords;
      // Also surface any string-only selectedLocation name, but prefer sessionLocName
      if (!locationString && selectedLocation && selectedLocation !== 'current') {
        locationString = selectedLocation.address || selectedLocation.name || '';
      }
    } else if (selectedLocation === 'current') {
      locationString = 'Current Location';
    } else if (selectedLocation) {
      locationString = selectedLocation.address;
      coords = selectedLocation.coordinates;
    }

    // Calculate scheduled time window if scheduling
    // Eatzy uses 15-minute windows (900 seconds), not 1-hour windows
    let scheduledStart: number | undefined;
    let scheduledEnd: number | undefined;
    if (timing === 'scheduled' && scheduledTime) {
      const scheduledDate = new Date(scheduledTime);
      scheduledStart = Math.floor(scheduledDate.getTime() / 1000);
      scheduledEnd = scheduledStart + 900; // 15-minute window (Eatzy standard)
    }

    onProceed({
      serviceType,
      timing,
      scheduledTime: timing === 'scheduled' ? scheduledTime : undefined,
      scheduledStart,
      scheduledEnd,
      location: locationString,
      coordinates: coords,
      radius: mode === 'discovery' && serviceType === 'takeout' ? radius : undefined,
      addressId: mode === 'order' && selectedAddress ? selectedAddress.id : undefined
    });
    onClose();
  };

  const handleLocationChange = (newLocation: 'current' | SavedLocation) => {
    setSelectedLocation(newLocation);
    setShowLocationModal(false);
    // Propagate back to parent so Restaurant's session state updates (avoids cross-hook staleness)
    if (newLocation !== 'current' && typeof newLocation === 'object') {
      onSessionLocationChange?.(
        newLocation.address || newLocation.name || '',
        newLocation.coordinates?.lat,
        newLocation.coordinates?.lng
      );
    }
  };

  const getLocationDisplay = () => {
    if (!selectedLocation) return 'Select location';
    if (selectedLocation === 'current') return 'Current Location';
    return selectedLocation.name || selectedLocation.address;
  };

  // Determine if proceed is disabled
  // Coverage check is informational - we only block if we positively know delivery is unavailable
  const isDeliveryUnavailable = mode === 'order' && serviceType === 'delivery' && coverageData && !coverageData.covered;
  const hasValidLocation = mode === 'order' 
    ? (
        serviceType !== 'delivery' ||
        selectedAddress !== null ||
        (noSavedAddresses && (
          !!sessionCoords ||
          // Accept a freshly-picked location that has actual coordinates (string-only seeds don't count)
          (selectedLocation !== null && selectedLocation !== 'current' && !!selectedLocation.coordinates)
        ))
      )
    : (selectedLocation !== null);
  // Additional validation: if restaurant is closed, user must select a scheduled time
  const needsScheduledTime = !isRestaurantOpen && timing === 'scheduled' && !scheduledTime;
  // For delivery, only block if coverage check explicitly says delivery is unavailable
  // Don't block on missing coordinates - address selection is sufficient, restaurant page can handle
  const isDeliveryBlocked = mode === 'order' && serviceType === 'delivery' && isDeliveryUnavailable;
  const isProceedDisabled = !hasValidLocation || isDeliveryBlocked || needsScheduledTime;

  // Get estimated time display - use ASAP quote for formatted time range
  // For delivery, only show when we have an address with coordinates
  // Note: This only shows for ASAP timing - when restaurant is closed (scheduled timing), no estimate is shown
  const getEstimatedTimeDisplay = () => {
    if (timing !== 'asap') return null;
    
    // For delivery, we need an address with coordinates to calculate accurate delivery time
    if (serviceType === 'delivery' && !coordinates) {
      return null; // Don't show estimate without address
    }
    
    if (!asapQuote) return null;
    return `${asapQuote.label} ${asapQuote.formattedRange}`;
  };

  const estimatedTime = getEstimatedTimeDisplay();
  
  // Show a message indicating we need an address for delivery time estimate
  const showAddressNeededForEstimate = mode === 'order' && timing === 'asap' && serviceType === 'delivery' && !coordinates && !asapLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90svh] flex flex-col" data-testid="order-initiation-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{mode === 'order' ? 'Configure Your Order' : 'Start Your Order'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 px-1">
          {/* Service Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Service Type</Label>
            <RadioGroup value={serviceType} onValueChange={(value) => setServiceType(value as 'delivery' | 'takeout')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="delivery" id="delivery" data-testid="radio-delivery" />
                <Label htmlFor="delivery" className="font-normal cursor-pointer">
                  Delivery - Get it delivered to your door
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="takeout" id="takeout" data-testid="radio-takeout" />
                <Label htmlFor="takeout" className="font-normal cursor-pointer">
                  Takeout - Pick it up yourself
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Estimated Time Display (Order Mode) */}
          {mode === 'order' && estimatedTime && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              <Clock className="w-4 h-4" />
              <span>Estimated {serviceType === 'delivery' ? 'delivery' : 'pickup'} time: <strong>{estimatedTime}</strong></span>
            </div>
          )}
          
          {/* Message when we need address for delivery estimate */}
          {showAddressNeededForEstimate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              <Clock className="w-4 h-4" />
              <span>Select a delivery address below to see estimated delivery time</span>
            </div>
          )}

          {/* Date/Time Selection */}
          <DateTimeSelector 
            onTimeSelect={handleTimeSelect} 
            isRestaurantOpen={isAsapActuallyAvailable}
            hoursJson={hoursJson}
            timezone={timezone}
            serviceType={serviceType}
            isAsapLoading={asapLoading}
          />

          {/* Location Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {serviceType === 'delivery' ? 'Delivery Address' : 'Your Location'}
            </Label>
            
            {/* Order Mode: Show dropdown of saved addresses */}
            {mode === 'order' && serviceType === 'delivery' ? (
              <>
                {addressesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 border rounded-md">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading addresses...
                  </div>
                ) : addresses.length === 0 ? (
                  /* No saved addresses — show session location, freshly-picked location, or a picker button.
                     sessionLocName updates via prop cascade (async); selectedLocation is local state (sync).
                     Show whichever is available so "Use without saving" reflects immediately. */
                  (() => {
                    const displayName = sessionLocName ||
                      (selectedLocation && selectedLocation !== 'current' ? selectedLocation.name || selectedLocation.address : null);
                    return displayName ? (
                      <button
                        type="button"
                        onClick={() => setShowLocationModal(true)}
                        className="w-full flex items-center gap-3 p-3 border rounded-md text-left hover-elevate"
                        data-testid="button-session-location"
                      >
                        <MapPin className="w-4 h-4 flex-shrink-0 text-[#0D9C97]" />
                        <span className="flex-1 text-sm truncate">{displayName}</span>
                        <span className="text-xs text-muted-foreground shrink-0">Change</span>
                      </button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setShowLocationModal(true)}
                        className="w-full justify-start gap-2 h-auto py-3"
                        data-testid="button-select-delivery-address"
                      >
                        <MapPin className="w-4 h-4" />
                        <span className="flex-1 text-left truncate text-muted-foreground">Select delivery address</span>
                      </Button>
                    );
                  })()
                ) : (
                  <Select 
                    value={selectedAddressId || ''} 
                    onValueChange={setSelectedAddressId}
                  >
                    <SelectTrigger data-testid="select-delivery-address" className="h-auto py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <SelectValue placeholder="Select delivery address">
                          {selectedAddress && (
                            <span className="text-left truncate">
                              {getAddressLabel(selectedAddress)}
                            </span>
                          )}
                        </SelectValue>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {addresses.map((addr) => (
                        <SelectItem 
                          key={addr.id} 
                          value={addr.id.toString()}
                          data-testid={`address-option-${addr.id}`}
                        >
                          <div className="flex flex-col items-start">
                            <span className="font-medium">
                              {addr.label || 'Address'}
                              {addr.isPrimary && (
                                <span className="ml-2 text-xs text-muted-foreground">(Default)</span>
                              )}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {formatAddressDisplay(addr)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Show selected address details */}
                {selectedAddress && (
                  <div className="text-sm text-muted-foreground px-1">
                    {formatAddressDisplay(selectedAddress)}
                  </div>
                )}
              </>
            ) : (
              /* Discovery Mode or Takeout: Use location modal */
              <Button
                variant="outline"
                onClick={() => setShowLocationModal(true)}
                className="w-full justify-start gap-2 h-auto py-3"
                data-testid="button-select-location"
              >
                <MapPin className="w-4 h-4" />
                <span className="flex-1 text-left truncate">
                  {getLocationDisplay()}
                </span>
              </Button>
            )}

            {/* Coverage Check (Order Mode + Delivery) */}
            {mode === 'order' && serviceType === 'delivery' && coordinates && (
              <div className="mt-2">
                {coverageLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking delivery availability...
                  </div>
                )}
                {coverageData && !coverageData.covered && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Sorry, delivery is not available to this address. Please try a different address or choose takeout.
                    </AlertDescription>
                  </Alert>
                )}
                {coverageData && coverageData.covered && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    Delivery available to this address
                  </div>
                )}
              </div>
            )}

            {/* Need coordinates warning for delivery (when address lacks lat/lng) */}
            {mode === 'order' && serviceType === 'delivery' && selectedAddress && !coordinates && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This address is missing location data. Please update it in your profile or select a different address.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Radius Selection for Takeout (Discovery Mode Only) */}
          {mode === 'discovery' && serviceType === 'takeout' && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Search Radius</Label>
              <Select value={radius.toString()} onValueChange={(value) => setRadius(Number(value))}>
                <SelectTrigger data-testid="select-radius">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mile</SelectItem>
                  <SelectItem value="2">2 miles</SelectItem>
                  <SelectItem value="5">5 miles</SelectItem>
                  <SelectItem value="10">10 miles</SelectItem>
                  <SelectItem value="25">25 miles</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 flex-shrink-0 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            className="flex-1"
            disabled={isProceedDisabled}
            data-testid="button-proceed"
          >
            {coverageLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : mode === 'order' ? (
              'Take me to the menu'
            ) : (
              'Show me available restaurants'
            )}
          </Button>
        </div>
      </DialogContent>

      {/* Location Selection Modal */}
      {showLocationModal && (
        <LocationSelectionModal
          isOpen={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onLocationSelect={handleLocationChange}
          selectedLocation={
            selectedLocation === 'current' 
              ? 'Current Location' 
              : selectedLocation?.name || selectedLocation?.address || ''
          }
        />
      )}
    </Dialog>
  );
}
